import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SearchBar = ({ onSearch, onStockSelect }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Mock stock data for suggestions
  const mockStocks = [
    { symbol: 'AAPL', name: 'Apple Inc.', price: 175.43, sector: 'Technology' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.', price: 2847.63, sector: 'Technology' },
    { symbol: 'MSFT', name: 'Microsoft Corporation', price: 378.85, sector: 'Technology' },
    { symbol: 'AMZN', name: 'Amazon.com Inc.', price: 3342.88, sector: 'Consumer Discretionary' },
    { symbol: 'TSLA', name: 'Tesla Inc.', price: 248.50, sector: 'Consumer Discretionary' },
    { symbol: 'NVDA', name: 'NVIDIA Corporation', price: 875.28, sector: 'Technology' },
    { symbol: 'META', name: 'Meta Platforms Inc.', price: 484.20, sector: 'Technology' },
    { symbol: 'NFLX', name: 'Netflix Inc.', price: 486.81, sector: 'Communication Services' },
    { symbol: 'JPM', name: 'JPMorgan Chase & Co.', price: 145.67, sector: 'Financial Services' },
    { symbol: 'V', name: 'Visa Inc.', price: 234.89, sector: 'Financial Services' }
  ];

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);
    
    if (value.length > 0) {
      setIsLoading(true);
      // Simulate API call delay
      setTimeout(() => {
        const filtered = mockStocks.filter(stock =>
          stock.symbol.toLowerCase().includes(value.toLowerCase()) ||
          stock.name.toLowerCase().includes(value.toLowerCase()) ||
          stock.sector.toLowerCase().includes(value.toLowerCase())
        );
        setSuggestions(filtered.slice(0, 8));
        setIsOpen(true);
        setIsLoading(false);
      }, 200);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query.trim());
      setIsOpen(false);
    }
  };

  const handleSuggestionClick = (stock) => {
    setQuery(stock.symbol);
    setIsOpen(false);
    onStockSelect(stock);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && suggestions[selectedIndex]) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleClear = () => {
    setQuery('');
    setSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    if (query.length > 0 && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={suggestionsRef}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <Icon 
            name="Search" 
            size={20} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder="Search stocks by symbol, name, or sector..."
            className="w-full pl-10 pr-20 py-3 bg-card border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
            {query && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClear}
                className="h-8 w-8"
              >
                <Icon name="X" size={16} />
              </Button>
            )}
            
            <Button
              type="submit"
              variant="default"
              size="sm"
              disabled={!query.trim()}
            >
              <Icon name="Search" size={16} />
            </Button>
          </div>
        </div>
      </form>

      {/* Suggestions Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-dropdown mt-1 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-muted-foreground">Searching...</span>
              </div>
            </div>
          ) : suggestions.length > 0 ? (
            <div className="py-2">
              {suggestions.map((stock, index) => (
                <button
                  key={stock.symbol}
                  type="button"
                  onClick={() => handleSuggestionClick(stock)}
                  className={`w-full px-4 py-3 text-left hover:bg-muted transition-colors ${
                    index === selectedIndex ? 'bg-muted' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <span className="text-xs font-mono font-semibold text-primary">
                          {stock.symbol.slice(0, 2)}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{stock.symbol}</p>
                        <p className="text-sm text-muted-foreground">{stock.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-foreground">₹{stock.price.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">{stock.sector}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : query.length > 0 && !isLoading ? (
            <div className="p-4 text-center text-muted-foreground">
              <Icon name="Search" size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No stocks found for "{query}"</p>
              <p className="text-xs mt-1">Try searching by symbol, company name, or sector</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchBar;